import { AxiosError, AxiosRequestConfig } from 'axios';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { getAccessToken } from 'services/accessTokenServices';
import api from 'services/api';
import { InstallBaseData, getInstallBases } from 'services/basesServices';

type ErrorData = {
  response: {
    data: {
      message: string;
    };
    status: string;
  };
};

export type NextDateToRefuelData = {
  date: string;
};

type BasesContextType = {
  isLoading: boolean;
  bases: InstallBaseData[];
  selectedBase: InstallBaseData | null;
  getBases: () => void;
  addSelectedBase: (baseName: string) => void;
  removeSelectedBase: () => void;
  isUniqueBase: boolean;
  nextRefuelDate: string;
};

type ResponseData = {
  items: {
    id_evento: number;
    cia: string;
    filial: string;
    cliente: number;
    endereco: number;
    base_instalada: string;
    tipo_evento: string;
    evento: string;
    dt: string;
    creation_date: string;
  }[];
};

type BasesProviderProps = {
  children: React.ReactNode;
};

export type BaseData = {
  suplayWay: string;
  suplayDays: string;
  suplayFrequency: string;
  numberOfDaysToSuplay: number;
};

export type BaseTerritoryData = {
  base: BaseData;
  friday: boolean;
  monday: boolean;
  name: string;
  saturday: boolean;
  sunday: boolean;
  thursday: boolean;
  tuesday: boolean;
  useNewRefuelRule: boolean;
  wednesday: boolean;
};

export const BasesContext = createContext<BasesContextType>(
  {} as BasesContextType
);

export const BasesProvider: React.FC<BasesProviderProps> = ({ children }) => {
  const [bases, setBases] = useState<InstallBaseData[]>([]);
  const [basesPreview, setBasesPreview] = useState<InstallBaseData[]>([]);
  const [selectedBase, setSelectedBase] = useState<InstallBaseData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [nextRefuelDate, setNextRefuelDate] = useState<string>('');

  const isUniqueBase = useMemo(() => bases.length === 1, [bases]);

  const parseDateString = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  const formatDate = (dateString: string): string => {
    const date = parseDateString(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const getNextDateToRefuel = async (
    addressId: string,
    baseId: string
  ): Promise<NextDateToRefuelData[]> => {
    const access_token = await getAccessToken();

    const responseDate: NextDateToRefuelData[] = [];

    const header = {
      'Content-Type': 'application/json',
      access_token,
    };

    const parameters = {
      method: 'GET',
      headers: header,
    } as AxiosRequestConfig;

    const customerCode = localStorage.getItem(
      '@UltragazEmpresarial:customerCode'
    ) as string;

    const params = `${customerCode}/address/${addressId}/base/${baseId}`;
    const urlRequest = `logistics-operation/refuel-communication/v1/preview/customer/${params}`;

    try {
      const response = await api.get<ResponseData>(urlRequest, parameters);
      const { items } = response.data;
      let nexDate = '';
      if (items.length > 0) {
        const dateRefuel = items[items.length - 1].dt;
        nexDate = new Date(dateRefuel).toLocaleDateString('pt-BR');
        responseDate.push({
          date: nexDate,
        });
      }
      return responseDate;
    } catch (error) {
      const errorResponse = error as AxiosError | ErrorData;
      if (errorResponse?.response) {
        const { message } = errorResponse.response.data;
        if (message) {
          toast.error(message);
        } else {
          toast.error('Ocorreu um erro interno na aplicação');
        }
      }
    }
    return responseDate;
  };

  const getBaseTerritory = useCallback(
    async (baseParam: string): Promise<BaseTerritoryData> => {
      const access_token = await getAccessToken();
      let basesTerritoryData: BaseTerritoryData = {} as BaseTerritoryData;

      const header = {
        'Content-Type': 'application/json',
        access_token,
      };

      const parameters = {
        method: 'GET',
        headers: header,
      } as AxiosRequestConfig;

      try {
        const response = await api.get(
          `industrial/customer/v1/customers/me/bases/${baseParam}/territory`,
          parameters
        );
        basesTerritoryData = response.data;
      } catch (error) {
        const errorResponse = error as AxiosError | ErrorData;
        if (errorResponse?.response) {
          const { message } = errorResponse.response.data;
          if (message) {
            toast.error(message);
          } else {
            toast.error('Ocorreu um erro interno na aplicação');
          }
        }
      }

      return basesTerritoryData;
    },
    []
  );

  const getBases = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const response = await getInstallBases();
    const formattedResponse = response?.map((base) => ({
      ...base,
      nickname: base.nickname
        .split(' ')
        .map((word) => word.substring(0, 4))
        .join(' ')
        .substring(0, 14),
    }));
    setBasesPreview(formattedResponse as InstallBaseData[]);
    setIsLoading(false);
  }, []);

  const addSelectedBase = useCallback(
    (baseName: string): void => {
      const baseToAdd = bases.find((base) => base.name === baseName);
      if (baseToAdd) {
        setSelectedBase(baseToAdd);
      }
    },
    [bases]
  );

  const removeSelectedBase = useCallback((): void => {
    setSelectedBase(null);
  }, []);

  const getActivedBases = useCallback(
    (basesPreviewParam: InstallBaseData[]): void => {
      console.log({ basesPreviewParam });
      const newBases = basesPreviewParam.filter(async (base) => {
        const baseTerritoryResponse = await getBaseTerritory(base.name);
        return (
          baseTerritoryResponse?.base?.suplayWay?.toLocaleLowerCase() ===
          'agendado'
        );
      });

      if (newBases.length > 0) {
        setBases(newBases);
      } else {
        setBases(basesPreviewParam);
      }
      // setBases([
      //   {
      //     name: '2007127700.01',
      //     nickname: 'BASE COM VAPO',
      //     codeHierarchy: 'INSTALACAO',
      //     description: 'Teste',
      //     quantity: 1,
      //     status: 'A',
      //     activeDateFrom: '2020-07-17T00:00:00',
      //     creationDate: '2020-07-17T00:00:00',
      //     updateDate: '2023-06-29T18:00:16',
      //     address: 'AVENIDA FEODOR GURTOVENCO',
      //     number: '1431',
      //     complement: 'S C',
      //     addressCode: '8182475',
      //   },
      //   {
      //     name: '2007127700.01',
      //     nickname: 'BASE COM VAPO 2',
      //     codeHierarchy: 'INSTALACAO',
      //     description: 'Teste 2',
      //     quantity: 1,
      //     status: 'A',
      //     activeDateFrom: '2020-07-17T00:00:00',
      //     creationDate: '2020-07-17T00:00:00',
      //     updateDate: '2023-06-29T18:00:16',
      //     address: 'AVENIDA FEODOR GURTOVENCO',
      //     number: '1431',
      //     complement: 'S C',
      //     addressCode: '8182475',
      //   },
      // ]);
    },
    [getBaseTerritory]
  );

  useEffect(() => {
    getBases();
  }, [getBases]);

  useEffect(() => {
    getActivedBases(basesPreview);
  }, [basesPreview, getActivedBases]);

  useEffect(() => {
    if (isUniqueBase && !selectedBase && bases.length > 0) {
      addSelectedBase(bases[0].name);
    } else if (!isUniqueBase && bases.length > 1 && !selectedBase) {
      removeSelectedBase();
    }
  }, [isUniqueBase, bases, addSelectedBase, selectedBase, removeSelectedBase]);

  useEffect(() => {
    const getNextDate = async (): Promise<void> => {
      if (selectedBase) {
        const response = await getNextDateToRefuel(
          selectedBase.addressCode,
          selectedBase.name
        );

        if (response.length > 0) {
          setNextRefuelDate(formatDate(response[response.length - 1].date));
        }
      }
    };

    getNextDate();
  }, [selectedBase]);

  useEffect(() => {
    if (bases.length > 1) {
      const getBasesDates = async (): Promise<void> => {
        const basesDates = await Promise.all(
          bases.map(async (base) => {
            const response = await getNextDateToRefuel(
              base.addressCode,
              base.name
            );
            return response.map((date) => ({
              name: base.name,
              date: date.date,
            }));
          })
        );

        const dateOccurrences: any = {};
        basesDates.forEach((baseDateArray) => {
          baseDateArray.forEach(({ date }) => {
            if (dateOccurrences[date]) {
              dateOccurrences[date].push(baseDateArray[0].name);
            } else {
              dateOccurrences[date] = [baseDateArray[0].name];
            }
          });
        });

        const commonDateEntry = Object.entries(dateOccurrences).find(
          ([_, names]) => {
            // @ts-ignore
            return names.length > 1;
          }
        );

        let baseToSelect: InstallBaseData | undefined;

        if (commonDateEntry) {
          const [_, baseNames] = commonDateEntry;
          baseToSelect = bases.find((base) =>
            // @ts-ignore
            baseNames.includes(base.name)
          );

          if (baseToSelect) {
            setSelectedBase(baseToSelect);
          }
        }
      };

      getBasesDates();
    }
  }, [bases]);

  return (
    <BasesContext.Provider
      value={{
        isLoading,
        bases,
        selectedBase,
        getBases,
        addSelectedBase,
        removeSelectedBase,
        isUniqueBase,
        nextRefuelDate,
      }}
    >
      {children}
    </BasesContext.Provider>
  );
};

export function useBases(): BasesContextType {
  const context = useContext(BasesContext) as BasesContextType;
  return context;
}
